/**
 * FilePickerDialog — open file dialog using FS Sidecar.
 *
 * Usage:
 *   <FilePickerDialog
 *     open={isOpen}
 *     title="Open File"
 *     accept={[".md", ".markdown"]}
 *     onSelect={(path) => handleOpen(path)}
 *     onCancel={() => setOpen(false)}
 *   />
 */

import type { WsFsEntry } from "@magic-md/engine-client/protocol";
import { useT } from "@magic-md/i18n/react";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { workspaceFs } from "../../lib/engine-fs";
import type { RecentItem } from "../../store/recent-store";
import { DialogButton, DialogOverlay } from "./DialogOverlay";
import { FileBrowser } from "./FileBrowser";

export interface FilePickerDialogProps {
    open: boolean;
    title?: string;
    accept?: string[];
    onSelect: (filePath: string) => void;
    onCancel: () => void;
    /** Optional direct-open handler for recent workspace/folder shortcuts. */
    onOpenRecentWorkspace?: (item: RecentItem) => void | Promise<void>;
    /** Initial directory. Defaults to cwd. */
    initialPath?: string;
}

export function FilePickerDialog({
    open,
    title,
    accept,
    onSelect,
    onCancel,
    onOpenRecentWorkspace,
    initialPath,
}: FilePickerDialogProps) {
    const t = useT("editor");
    const [currentPath, setCurrentPath] = useState(initialPath ?? "");
    const [selectedEntry, setSelectedEntry] = useState<WsFsEntry | null>(null);

    const [resolving, setResolving] = useState(false);
    const [openingRecentItem, setOpeningRecentItem] = useState<RecentItem | null>(null);
    const [openRecentError, setOpenRecentError] = useState<string | null>(null);
    const openingRecentRef = useRef(false);
    const busy = openingRecentItem !== null;

    // Resolve initial path
    useEffect(() => {
        if (!open) return;
        setSelectedEntry(null);
        setOpeningRecentItem(null);
        setOpenRecentError(null);
        openingRecentRef.current = false;
        if (initialPath) {
            setCurrentPath(initialPath);
        } else {
            setResolving(true);
            workspaceFs
                .defaultDir()
                .then(setCurrentPath)
                .finally(() => setResolving(false));
        }
    }, [open, initialPath]);

    const handleSelect = useCallback((entry: WsFsEntry) => {
        if (entry.isDirectory) {
            // Single click on directory: just navigate
            setSelectedEntry(null);
        } else {
            setSelectedEntry(entry);
        }
    }, []);

    const handleFileOpen = useCallback(
        (entry: WsFsEntry) => {
            if (!entry.isDirectory) {
                onSelect(entry.path);
            }
        },
        [onSelect],
    );

    const handleNavigate = useCallback((path: string) => {
        setCurrentPath(path);
        setSelectedEntry(null);
    }, []);

    const handleOpenRecentWorkspace = useCallback(
        async (item: RecentItem) => {
            if (!onOpenRecentWorkspace || openingRecentRef.current) return;
            openingRecentRef.current = true;
            setOpeningRecentItem(item);
            setOpenRecentError(null);
            try {
                await onOpenRecentWorkspace(item);
                onCancel();
            } catch (err) {
                openingRecentRef.current = false;
                setOpeningRecentItem(null);
                setOpenRecentError(err instanceof Error ? err.message : String(err));
            }
        },
        [onCancel, onOpenRecentWorkspace],
    );

    const handleConfirm = () => {
        if (!openingRecentItem && selectedEntry && !selectedEntry.isDirectory) {
            onSelect(selectedEntry.path);
        }
    };

    return (
        <DialogOverlay
            title={title ?? t("dialog.openFile")}
            open={open}
            onClose={onCancel}
            closeDisabled={busy}
            className="w-[820px]"
            footer={
                <>
                    <DialogButton onClick={onCancel} disabled={busy}>
                        {t("common.cancel")}
                    </DialogButton>
                    <DialogButton
                        variant="primary"
                        onClick={handleConfirm}
                        disabled={busy || !selectedEntry}
                    >
                        {t("common.open")}
                    </DialogButton>
                </>
            }
        >
            {resolving ? (
                <div className="h-[400px] flex items-center justify-center">
                    <Loader2 size={24} className="animate-spin text-muted-foreground" />
                </div>
            ) : !currentPath ? (
                <div className="h-[400px] flex items-center justify-center text-sm text-muted-foreground">
                    {t("dialog.noAccessibleRoots")}
                </div>
            ) : (
                <>
                    <div className="relative h-[420px]" aria-busy={openingRecentItem ? "true" : undefined}>
                        <FileBrowser
                            currentPath={currentPath}
                            onNavigate={handleNavigate}
                            onSelect={handleSelect}
                            onFileOpen={handleFileOpen}
                            onOpenRecentWorkspace={
                                onOpenRecentWorkspace ? handleOpenRecentWorkspace : undefined
                            }
                            mode="file"
                            accept={accept}
                            selectedPath={selectedEntry?.path}
                        />
                        {openingRecentItem ? (
                            <div
                                className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-background/85 px-6 text-center backdrop-blur-[1px]"
                                role="status"
                                aria-live="polite"
                            >
                                <Loader2 size={22} className="animate-spin text-primary" />
                                <div className="text-sm font-medium text-foreground">
                                    {openingRecentItem.type === "workspace"
                                        ? t("dialog.openingWorkspace")
                                        : t("dialog.openingFolder")}
                                </div>
                                <div className="max-w-[520px] truncate font-mono text-[11px] text-muted-foreground">
                                    {openingRecentItem.path}
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {openRecentError ? (
                        <div className="border-t border-border px-4 py-2 text-xs text-destructive">
                            {t("dialog.openFailed", { message: openRecentError.slice(0, 140) })}
                        </div>
                    ) : null}

                    {/* Selected file indicator */}
                    {selectedEntry && !openRecentError && (
                        <div className="px-4 py-2 border-t border-border text-xs text-muted-foreground truncate">
                            {selectedEntry.path}
                        </div>
                    )}
                </>
            )}
        </DialogOverlay>
    );
}
